import type { NotificationsResponse, StaffNotification } from '../services/notificationService';
import type { CallbackTicket } from '../services/callbackTicketService';
import { META_ACCOUNT_QUALITY_URL } from './channelHealth';
import { findTicketForCall, isPhoneCallbackNotification, ticketUrl } from './callbackTicket';
import type { OpenTarget } from './chimeScheduler';

/**
 * Pure state of the bell's notification list.
 *
 * The server's `unread_count` covers ALL unread entries, the list only the
 * newest ones. `outsideUnread` is the part beyond the list, so the badge is
 * `outsideUnread + unread in the list` — and every local change (live upsert,
 * read here or in another tab) moves it correctly without a refetch.
 */

export const MAX_NOTIFICATIONS = 50;

export interface NotificationListState {
  items: StaffNotification[];
  outsideUnread: number;
}

export const EMPTY_NOTIFICATION_LIST: NotificationListState = { items: [], outsideUnread: 0 };

export type ReadTarget = number[] | 'all';

/**
 * Counts toward the badge: not read AND not resolved — the server's
 * `unread_count` rule. A resolved entry (finished call, handled request) needs
 * nobody's attention any more, read or not.
 */
export const isUnread = (item: StaffNotification) => !item.read_at && !item.resolved_at;

function time(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

const byNewest = (a: StaffNotification, b: StaffNotification) => time(b.updated_at) - time(a.updated_at) || b.id - a.id;

export function unreadTotal(state: NotificationListState): number {
  return state.outsideUnread + state.items.filter(isUnread).length;
}

export function hasUrgentUnread(state: NotificationListState): boolean {
  return state.items.some((item) => item.urgent && isUnread(item) && !item.resolved_at);
}

/** Keep the list bounded; unread entries that fall off stay in the badge. */
function capped(items: StaffNotification[], outsideUnread: number): NotificationListState {
  const sorted = [...items].sort(byNewest);
  const kept = sorted.slice(0, MAX_NOTIFICATIONS);
  const dropped = sorted.slice(MAX_NOTIFICATIONS).filter(isUnread).length;
  return { items: kept, outsideUnread: outsideUnread + dropped };
}

export function fromResponse(response: NotificationsResponse | null | undefined): NotificationListState {
  const items = Array.isArray(response?.items) ? response!.items.filter((i) => i && typeof i.id === 'number') : [];
  const unique = Array.from(new Map(items.map((i) => [i.id, i])).values());
  const listUnread = unique.filter(isUnread).length;
  const serverUnread = typeof response?.unread_count === 'number' ? response.unread_count : listUnread;
  return capped(unique, Math.max(0, serverUnread - listUnread));
}

/**
 * Newer `updated_at` wins. On a tie read/resolved never fall back to null —
 * the only way back to "unread" is our own rollback (`revertRead`).
 */
function merge(existing: StaffNotification, incoming: StaffNotification): StaffNotification {
  const diff = time(incoming.updated_at) - time(existing.updated_at);
  if (diff < 0) return existing;
  if (diff > 0) return incoming;
  return {
    ...incoming,
    read_at: incoming.read_at ?? existing.read_at,
    resolved_at: incoming.resolved_at ?? existing.resolved_at,
  };
}

export function upsertNotification(state: NotificationListState, incoming: StaffNotification): NotificationListState {
  if (!incoming || typeof incoming.id !== 'number') return state;
  const existing = state.items.find((i) => i.id === incoming.id);
  if (!existing) return capped([...state.items, incoming], state.outsideUnread);
  const merged = merge(existing, incoming);
  if (merged === existing) return state;
  return capped(
    state.items.map((i) => (i.id === incoming.id ? merged : i)),
    state.outsideUnread,
  );
}

export function applyRead(state: NotificationListState, target: ReadTarget, readAt: string): NotificationListState {
  const ids = target === 'all' ? null : new Set(target);
  let changed = false;
  const items = state.items.map((item) => {
    if (item.read_at || (ids && !ids.has(item.id))) return item;
    changed = true;
    return { ...item, read_at: readAt };
  });
  const outsideUnread = target === 'all' ? 0 : state.outsideUnread;
  if (!changed && outsideUnread === state.outsideUnread) return state;
  return { items, outsideUnread };
}

/** Undo OUR optimistic read (identified by its stamp); a read from elsewhere stays. */
export function revertRead(
  state: NotificationListState,
  target: ReadTarget,
  readAt: string,
  outsideBefore: number,
): NotificationListState {
  const ids = target === 'all' ? null : new Set(target);
  const items = state.items.map((item) =>
    item.read_at === readAt && (!ids || ids.has(item.id)) ? { ...item, read_at: null } : item,
  );
  return { items, outsideUnread: target === 'all' ? outsideBefore : state.outsideUnread };
}

/** After `POST …/read`: the server's total is the truth for everything outside the list. */
export function withServerUnread(state: NotificationListState, serverUnread: number): NotificationListState {
  const listUnread = state.items.filter(isUnread).length;
  const outsideUnread = Math.max(0, serverUnread - listUnread);
  return outsideUnread === state.outsideUnread ? state : { ...state, outsideUnread };
}

/** Unread entries that belong to this chat/call. */
export function unreadIdsFor(state: NotificationListState, target: OpenTarget): number[] {
  return state.items
    .filter(
      (item) =>
        isUnread(item) &&
        ((target.contactKey && item.contact_key === target.contactKey) ||
          (target.callSid && item.call_sid === target.callSid)),
    )
    .map((item) => item.id);
}

// ---- Links (the Verläufe strand reads these URL parameters) ----------------

export function whatsappChatUrl(contactKey: string): string {
  return `/conversations?${new URLSearchParams({ contact: contactKey, focus: 'composer' }).toString()}`;
}

export function callUrl(callSid: string): string {
  return `/conversations?${new URLSearchParams({ call: callSid }).toString()}`;
}

export function conversationUrl(conversationId: number): string {
  return `/conversations?${new URLSearchParams({ c: String(conversationId) }).toString()}`;
}

type LinkFields = Pick<StaffNotification, 'contact_key' | 'call_sid'> &
  Partial<Pick<StaffNotification, 'kind' | 'conversation_id'>>;

/**
 * Where an entry opens in the app. A phone callback wish opens its ticket's
 * call: the stored transcript as soon as the open ticket knows it (the bell
 * entry is written during the call, before the record exists), else the
 * entry's own record, else the call by its sid.
 */
export function notificationUrl(item: LinkFields, tickets: CallbackTicket[] = []): string | null {
  if (item.contact_key) return whatsappChatUrl(item.contact_key);
  if (item.kind && isPhoneCallbackNotification({ ...item, kind: item.kind })) {
    const ticket = findTicketForCall(tickets, item.call_sid);
    if (ticket) return ticketUrl(ticket);
    if (item.conversation_id != null) return conversationUrl(item.conversation_id);
  }
  if (item.call_sid) return callUrl(item.call_sid);
  return null;
}

/**
 * Where an entry leads OUTSIDE the app: a blocked WhatsApp number has no chat
 * to jump to, the salon checks it at Meta. null for everything that opens in the app.
 */
export function notificationExternalUrl(item: Pick<StaffNotification, 'kind'>): string | null {
  return item.kind === 'whatsapp_channel_blocked' ? META_ACCOUNT_QUALITY_URL : null;
}

/** A new tab that cannot reach back into this one. */
export function openExternalUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Which chat/call a Verläufe URL has open, or null. A stored call (`?c=`)
 * counts as its call when an open Rückruf-Ticket belongs to it, so opening the
 * ticket marks its bell entry seen — like opening a WhatsApp chat does.
 */
export function openTargetFromLocation(
  pathname: string,
  search: string,
  tickets: CallbackTicket[] = [],
): OpenTarget | null {
  if (!pathname.startsWith('/conversations')) return null;
  const params = new URLSearchParams(search);
  const contactKey = params.get('contact');
  const callSid = params.get('call') || (contactKey ? null : ticketCallOfStored(params.get('c'), tickets));
  return contactKey || callSid ? { contactKey, callSid } : null;
}

function ticketCallOfStored(conversationId: string | null, tickets: CallbackTicket[]): string | null {
  if (!conversationId || !/^\d+$/.test(conversationId)) return null;
  return tickets.find((t) => t.conversation_id === Number(conversationId))?.call_sid ?? null;
}

// ---- Tab title ---------------------------------------------------------------

const PREFIX = /^\(\d+\+?\)\s/;

export function stripUnreadPrefix(title: string): string {
  return title.replace(PREFIX, '');
}

export function titleWithUnread(title: string, unread: number): string {
  const base = stripUnreadPrefix(title);
  if (unread <= 0) return base;
  return `(${unread > 99 ? '99+' : unread}) ${base}`;
}
